import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { requestIdleCallback } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#scheduleMediaPrewarm();
  }

  #controller = new AbortController();

  #prewarmedHighResolutionImages = new Set();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  #scheduleMediaPrewarm() {
    requestAnimationFrame(() => {
      requestIdleCallback(() => {
        if (!this.isConnected) return;

        const images = this.#getPrewarmImages();
        if (!images.length) return;

        const priorityImages = this.#getPriorityImages(images);
        priorityImages.forEach((image) => this.#prewarmImage(image, true));

        const remainingImages = images.filter((image) => !priorityImages.includes(image));
        remainingImages.forEach((image, index) => {
          window.setTimeout(() => {
            if (!this.isConnected) return;
            this.#prewarmImage(image, false);
          }, 120 * index);
        });
      });
    });
  }

  #getPrewarmImages() {
    return Array.from(this.querySelectorAll('img.product-media__image')).filter((image) => image instanceof HTMLImageElement);
  }

  #getPriorityImages(images) {
    const currentIndex = Number(this.slideshow?.current || 0);
    const orderedIndexes = [currentIndex, currentIndex + 1, currentIndex - 1].filter(
      (index, position, indexes) => index >= 0 && index < images.length && indexes.indexOf(index) === position
    );

    return orderedIndexes.map((index) => images[index]).filter(Boolean);
  }

  #prewarmImage(image, decode) {
    image.loading = 'eager';
    image.decoding = 'async';

    if (decode && image.decode) {
      image.decode().catch(() => {});
    }

    this.#prewarmHighResolutionImage(image, decode);
  }

  #prewarmHighResolutionImage(image, decode) {
    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#prewarmedHighResolutionImages.has(highResolutionUrl)) return;

    this.#prewarmedHighResolutionImages.add(highResolutionUrl);

    const highResolutionImage = new Image();
    highResolutionImage.decoding = 'async';
    highResolutionImage.src = highResolutionUrl;

    if (decode && highResolutionImage.decode) {
      highResolutionImage.decode().catch(() => {});
    }
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
